import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {useDispatch} from "react-redux"
import {useTranslation} from "react-i18next"

import { Preloader } from "@vedmarket/ui-kit"
import DemoHeader from './page/header'

import Footer from "./page/footer"
import DemoHero from "./page/hero"
import DemoPlayer from "./player"

import {useLazyGetDictionaryLanguagesQuery} from "../../../api/localization"
import {changeAppLanguage, getLanguageCode} from "../../../config/i18"
import {
  hydrateTestDriveOrder,
  useLazyGetTestDriveScenarioQuery,
  useLazyGetTestDriveScenariosQuery,
} from "../../../api/test_drive";
import {
  DEMO_ALLOWED_SELECTOR,
  DEMO_INTERACTION_EVENTS,
  DEMO_RESTRICTED_SELECTOR,
} from "./interaction-constants";
import {normalizeDemoScenarioDetails, setDemoScenarioDetails} from "./scenario"
import {useAuthContext} from "../../../context/AuthContext"
import {ROUTES} from "../../../config/constants"

import './demo.scss'
import './desktop.scss'
import './large.scss'
import './medium.scss'
import './small.scss'
import './extra-small.scss'
import './calculator-canvas.scss'
import './hero-layout.scss'
import './hero-categories.scss'
import './hero-artwork.scss'

const REGISTRATION_URL = 'https://my.vedmarket.ru/register'
const NON_USER_INTERACTION_EVENTS = [ 'focusin', 'submit' ]

const DEMO_EDITABLE_FIELD_SELECTOR_MAP: Record<string, string> = {
  quantity: '[data-demo-editable="pieces"]',
  unit_price: '[data-demo-editable="unit_price"]',
}

const getDemoEditableSelector = (editableFields?: string[]) =>
  editableFields
    ?.map((field) => DEMO_EDITABLE_FIELD_SELECTOR_MAP[field])
    .filter(Boolean)
    .join(', ') || ''

const shouldBlockDemoAction = (
  target: Element,
  editableSelector: string,
  root?: HTMLDivElement | null
) => {
  const isEditable = Boolean(editableSelector && target.closest(editableSelector))
  const isAllowed = isEditable || Boolean(target.closest(DEMO_ALLOWED_SELECTOR))
  const isRestricted = Boolean(root?.contains(target) && target.closest(DEMO_RESTRICTED_SELECTOR))

  return isRestricted && !isAllowed
}

const DemoCalculator = () => {

  const { user } = useAuthContext()
  const { i18n } = useTranslation()

  const isRegistered = Boolean(user?.id)

  const demoRef = useRef<HTMLDivElement>(null)
  const hydratedScenarioRef = useRef<string>()

  const dispatch = useDispatch()

  const [ selectedScenarioSlug, setSelectedScenarioSlug ] = useState<string>()
  const [ hydratedScenarioSlug, setHydratedScenarioSlug ] = useState<string>()
  const [ introductionShown, setIntroductionShown ] = useState(false)

  const [ localizationGetLanguagesRequest, { data: languages } ] = useLazyGetDictionaryLanguagesQuery()
  const [ getScenariosRequest, { data: scenarios = [] } ] = useLazyGetTestDriveScenariosQuery()
  const [ getScenarioRequest, { data: scenarioDetails } ] = useLazyGetTestDriveScenarioQuery()
  const isCurrentScenario = Boolean(selectedScenarioSlug && scenarioDetails?.slug === selectedScenarioSlug)
  const isHydratedScenario = isCurrentScenario && hydratedScenarioSlug === selectedScenarioSlug
  const shouldShowCalculator = Boolean(scenarioDetails && isHydratedScenario)
  const accountActionUrl = isRegistered ? `/${ROUTES.CABINET}/${ROUTES.CALCULATIONS}` : REGISTRATION_URL
  const selectedLanguage = languages?.find(({ code }) => getLanguageCode(code) === getLanguageCode(i18n.language))?.code

  const demoEditableSelector = useMemo(
    () => getDemoEditableSelector(scenarioDetails?.editable_fields),
    [ scenarioDetails?.editable_fields ]
  )

  useEffect(() => {
    window.dispatchEvent(new Event('resize'))

    return () => {
      window.dispatchEvent(new Event('resize'))
    }
  }, [])

  useEffect(() => {
    localizationGetLanguagesRequest('')
    getScenariosRequest()
  }, [ i18n.language, localizationGetLanguagesRequest, getScenariosRequest ])

  useEffect(() => {
    if (selectedScenarioSlug) {
      getScenarioRequest(selectedScenarioSlug)
    }
  }, [ i18n.language, selectedScenarioSlug, getScenarioRequest ])

  useEffect(() => {
    const firstScenarioSlug = scenarios[0]?.slug

    if (!firstScenarioSlug) {
      return
    }

    const isSelectedScenarioAvailable = scenarios.some(({ slug }) => slug === selectedScenarioSlug)

    if (!selectedScenarioSlug || !isSelectedScenarioAvailable) {
      setSelectedScenarioSlug(firstScenarioSlug)
    }
  }, [ scenarios, selectedScenarioSlug ])

  useEffect(() => {
    if (!selectedScenarioSlug || scenarioDetails?.slug !== selectedScenarioSlug) {
      return
    }

    let isCurrent = true

    const normalizedScenarioDetails = normalizeDemoScenarioDetails(scenarioDetails)

    setDemoScenarioDetails(normalizedScenarioDetails)

    const shouldReset = hydratedScenarioRef.current !== selectedScenarioSlug

    hydrateTestDriveOrder({ dispatch, order: normalizedScenarioDetails.order, shouldReset }).then(() => {
      if (isCurrent) {
        hydratedScenarioRef.current = selectedScenarioSlug
        setHydratedScenarioSlug(selectedScenarioSlug)
      }
    })

    return () => {
      isCurrent = false
    }
  }, [ dispatch, scenarioDetails, selectedScenarioSlug ])

  const handleSelectScenario = (slug: string) => {
    if (slug === selectedScenarioSlug) {
      return
    }

    setSelectedScenarioSlug(slug)
    setHydratedScenarioSlug(undefined)
    setDemoScenarioDetails(undefined)
  }

  useEffect(() => {
    const handleRestrictedInteraction = (event: Event) => {
      const target = event.target as Element

      const isPlaybackInteraction = !event.isTrusted || NON_USER_INTERACTION_EVENTS.includes(event.type)

      if (isPlaybackInteraction) {
        return
      }

      if (!shouldBlockDemoAction(target, demoEditableSelector, demoRef.current)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      target.closest<HTMLElement>(DEMO_RESTRICTED_SELECTOR)?.blur()
    }

    DEMO_INTERACTION_EVENTS.forEach(eventName => {
      document.addEventListener(eventName, handleRestrictedInteraction, true)
    })

    return () => {
      DEMO_INTERACTION_EVENTS.forEach(eventName => {
        document.removeEventListener(eventName, handleRestrictedInteraction, true)
      })
    }
  }, [ demoEditableSelector ])

  return (
    <div
      ref={demoRef}
      className='demo'
    >
      <DemoHeader
        isDemo
        isDemoRegistered={isRegistered}
        isEmpty
        languages={languages}
        user={{ lang: selectedLanguage || getLanguageCode(i18n.language) } as MeParamsOutput}
        changeLanguage={changeAppLanguage}
      />
      <DemoHero
        backgroundImage={scenarioDetails?.slug === selectedScenarioSlug ? scenarioDetails?.background_image : undefined}
        scenarios={scenarios}
        selectedScenarioSlug={selectedScenarioSlug}
        onSelectScenario={handleSelectScenario}
      />
      <div className='demo__window'>
        <div className='demo__window-chrome' aria-hidden='true'>
          <span className='demo__window-dot demo__window-dot_red' />
          <span className='demo__window-dot demo__window-dot_yellow' />
          <span className='demo__window-dot demo__window-dot_green' />
        </div>
        {shouldShowCalculator && scenarioDetails ? (
          <DemoPlayer key={selectedScenarioSlug} scenario={scenarioDetails} introductionShown={introductionShown} onIntroductionComplete={() => setIntroductionShown(true)} />
        ) : (
          <main className='demo__content'>
            <Preloader />
          </main>
        )}
      </div>
      <Footer
        isDesktop
        isRegistered={isRegistered}
        accountActionUrl={accountActionUrl}
      />
    </div>
  )
}

export default DemoCalculator
